import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import {
  Area,
  AreaChart,
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { AlertCircle, Clock, Snowflake, ShieldAlert } from "lucide-react";
import KPICard from "@/components/KPICard";
import TopBar from "@/components/TopBar";
import { Dashboard } from "@/api/endpoints";
import { eur, fmtDayShort } from "@/lib/format";
import { isLost, stageRole, usePalette, type Role } from "@/lib/colors";

const RANGES: { label: string; days: number }[] = [
  { label: "30 d", days: 30 },
  { label: "90 d", days: 90 },
  { label: "180 d", days: 180 },
  { label: "1 y", days: 365 },
];

const HEALTH_ICONS: Record<string, typeof AlertCircle> = {
  "Past-due actions": AlertCircle,
  "Stuck 30+ days in stage": Clock,
  "Cold 14+ days no contact": Snowflake,
  "Blocked deals": ShieldAlert,
};

const HEALTH_ROLES: Record<string, Role> = {
  "Past-due actions": "pink",
  "Stuck 30+ days in stage": "amber",
  "Cold 14+ days no contact": "azure",
  "Blocked deals": "purple",
};

export default function DashboardPage() {
  const [tsDays, setTsDays] = useState(180);
  const p = usePalette();
  const { data: kpis } = useQuery({
    queryKey: ["dashboard"],
    queryFn: () => Dashboard.kpis(),
  });
  const { data: ts } = useQuery({
    queryKey: ["timeseries", tsDays],
    queryFn: () => Dashboard.timeseries(tsDays),
  });

  return (
    <>
      <TopBar title="Dashboard" />
      <div className="px-4 py-4 md:px-6 md:py-6 space-y-6">
        {/* Headline KPIs */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <KPICard
            label="Total pipeline ARR"
            value={eur(kpis?.total_arr, { short: true })}
            sublabel={`${kpis?.total_deals ?? 0} deals`}
            accent={p.azure}
            seed={1}
          />
          <KPICard
            label="Weighted ARR"
            value={eur(kpis?.weighted_arr, { short: true })}
            sublabel="probability × ARR"
            accent={p.green}
            seed={2}
          />
          <KPICard
            label="Signed ARR"
            value={eur(kpis?.signed_arr, { short: true })}
            sublabel={`${kpis?.signed_deals ?? 0} signed`}
            accent={p.violet}
            seed={3}
          />
          <KPICard
            label="Open ARR"
            value={eur(kpis?.open_arr, { short: true })}
            sublabel={`${kpis?.open_deals ?? 0} open`}
            accent={p.purple}
            seed={4}
          />
        </div>

        {/* Weighted ARR over time */}
        <div className="panel p-5">
          <div className="flex flex-wrap items-center justify-between gap-2 mb-4">
            <h2 className="section-title">Weighted ARR over time</h2>
            <div className="inline-flex items-center gap-0.5 bg-fg/[0.03] rounded-full p-0.5">
              {RANGES.map((r) => (
                <button
                  key={r.days}
                  onClick={() => setTsDays(r.days)}
                  className={
                    "px-2.5 py-1 rounded-full text-xs whitespace-nowrap transition " +
                    (tsDays === r.days
                      ? "bg-fg/[0.10] text-mut-100"
                      : "text-mut-300 hover:text-mut-100")
                  }
                >
                  {r.label}
                </button>
              ))}
            </div>
          </div>
          <div className="h-64">
            <ResponsiveContainer>
              <AreaChart
                data={(ts ?? []).map((p) => ({ ...p, weighted_arr: Math.round(p.weighted_arr) }))}
                margin={{ left: 10, right: 16, top: 10, bottom: 0 }}
              >
                <defs>
                  <linearGradient id="wArr" x1="0" x2="0" y1="0" y2="1">
                    <stop offset="0%" stopColor={p.azure} stopOpacity={0.45} />
                    <stop offset="100%" stopColor={p.azure} stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid stroke={p.grid} strokeDasharray="3 4" vertical={false} />
                <XAxis
                  dataKey="captured_on"
                  stroke={p.muted}
                  tickFormatter={fmtDayShort}
                  minTickGap={28}
                />
                <YAxis stroke={p.faint} tickFormatter={(v) => eur(v, { short: true })} />
                <Tooltip
                  formatter={(v: number) => eur(v)}
                  labelFormatter={(l: string) => fmtDayShort(l)}
                />
                <Area
                  type="monotone"
                  dataKey="weighted_arr"
                  stroke={p.azure}
                  strokeWidth={2}
                  fill="url(#wArr)"
                />
              </AreaChart>
            </ResponsiveContainer>
          </div>
          {ts && ts.length === 0 && (
            <div className="text-sm text-mut-300 mt-2">
              No snapshots yet — run <span className="kbd">POST /api/dashboard/snapshot</span>{" "}
              daily.
            </div>
          )}
        </div>

        {/* By stage / by owner charts */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <div className="panel p-5">
            <h2 className="section-title mb-4">ARR by stage</h2>
            <div className="h-72">
              <ResponsiveContainer>
                <BarChart
                  data={kpis?.by_stage ?? []}
                  layout="vertical"
                  margin={{ left: 10, right: 24 }}
                >
                  <CartesianGrid stroke={p.grid} strokeDasharray="3 4" horizontal={false} />
                  <XAxis
                    type="number"
                    stroke={p.faint}
                    tickFormatter={(v) => eur(v, { short: true })}
                  />
                  <YAxis type="category" dataKey="stage" stroke={p.muted} width={140} />
                  <Tooltip formatter={(v: number) => eur(v)} />
                  <Bar dataKey="arr" radius={[0, 6, 6, 0]}>
                    {(kpis?.by_stage ?? []).map((s) => (
                      <Cell
                        key={s.stage}
                        fill={isLost(s.stage) ? p.faint : p[stageRole(s.stage)]}
                      />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>

          <div className="panel p-5">
            <h2 className="section-title mb-4">Weighted ARR by owner</h2>
            <div className="h-72">
              <ResponsiveContainer>
                <BarChart data={kpis?.by_owner ?? []} margin={{ left: 10, right: 10 }}>
                  <CartesianGrid stroke={p.grid} strokeDasharray="3 4" vertical={false} />
                  <XAxis dataKey="owner" stroke={p.muted} />
                  <YAxis stroke={p.faint} tickFormatter={(v) => eur(v, { short: true })} />
                  <Tooltip formatter={(v: number) => eur(v)} />
                  <Bar dataKey="weighted_arr" fill={p.azure} radius={[6, 6, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>
        </div>

        {/* Health flags */}
        <div className="panel p-5">
          <h2 className="section-title mb-4">Pipeline hygiene</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {(kpis?.health ?? []).map((h) => {
              const Icon = HEALTH_ICONS[h.label] ?? AlertCircle;
              const color = p[HEALTH_ROLES[h.label] ?? "amber"];
              return (
                <div key={h.label} className="panel-tight p-4">
                  <div className="flex items-center gap-2 text-sm text-mut-300">
                    <Icon className="w-3.5 h-3.5" style={{ color }} /> {h.label}
                  </div>
                  <div className="text-3xl font-bold mt-2" style={{ color }}>
                    {h.count}
                  </div>
                  {h.arr_at_risk != null && (
                    <div className="text-sm text-mut-300 mt-1">
                      {eur(h.arr_at_risk, { short: true })} at risk
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </>
  );
}
